import { ProductGroupCountDTO } from '../../dto';
import { ProductGroupType } from '../GroupType';

export interface ProductGroup {
  id: number;
  type: ProductGroupType;
  groupNumber: string;
  name: string;
  count?: number;
  getLabel: () => string;
}

interface ProductGroupCountProps {
  id: number;
  type: ProductGroupType;
  groupNumber: string;
  name: string;
  count?: number;
  subGroups?: ProductGroupCount[];
}

export class ProductGroupCount {
  id: number;
  type: ProductGroupType;
  groupNumber: string;
  name: string;
  count?: number;
  subGroups?: ProductGroupCount[];

  constructor({ id, type, groupNumber, name, count, subGroups }: ProductGroupCountProps) {
    this.id = id;
    this.type = type;
    this.groupNumber = groupNumber;
    this.name = name;
    this.count = count;
    this.subGroups = subGroups;
  }

  static flatten = (overGroups: ProductGroupCount[]): ProductGroup[] =>
    overGroups.flatMap(({ id, type, groupNumber, name, count, getLabel, subGroups }) => [
      { id, type, groupNumber, name, count, getLabel },
      ...(subGroups ?? []).flatMap(
        ({ id, type, groupNumber, name, count, getLabel, subGroups }) => [
          { id, type, groupNumber, name, count, getLabel },
          ...(subGroups ?? []).map((underGroup) => ({ ...underGroup })),
        ]
      ),
    ]);

  static filterMatchingProductGroupsDeep = (
    overGroups: ProductGroupCount[],
    productGroupNumbers: Set<string>
  ): ProductGroupCount[] =>
    overGroups
      .map((overGroup) => ({
        ...overGroup,
        subGroups: (overGroup.subGroups ?? [])
          .map((mainGroup) => {
            const filteredProductGroups = (mainGroup.subGroups ?? []).filter((productGroup) =>
              productGroupNumbers.has(productGroup.groupNumber)
            );
            return {
              ...mainGroup,
              subGroups: filteredProductGroups,
            };
          })
          .filter(
            (mainGroup) =>
              mainGroup.subGroups.length > 0 || productGroupNumbers.has(mainGroup.groupNumber)
          ),
      }))
      .filter(
        (overGroup) =>
          overGroup.subGroups.length > 0 || productGroupNumbers.has(overGroup.groupNumber)
      );

  getLabel = (): string => (this.name ? `${this.groupNumber} ${this.name}` : this.groupNumber);

  static fromDto = (dto: ProductGroupCountDTO): ProductGroupCount =>
    new ProductGroupCount({
      id: dto.id,
      type: dto.type,
      groupNumber: dto.groupNumber,
      name: dto.name,
      count: dto.count,
      subGroups: dto.subGroups?.map(ProductGroupCount.fromDto),
    });
}
